"use client";

import { useEffect, useId, useMemo, useRef, useState, type RefObject } from "react";

import {
  buildMarkerArcPath,
  globeLayout,
  projectLngLat,
  type GlobeMarker,
  type GlobeRotation,
} from "@/src/login/globe-projection";
import type { ThemeMode } from "@/store/theme-store";

type ActivePath = {
  id: string;
  fromIdx: number;
  toIdx: number;
  duration: number;
  startedAt: number;
};

const PATH_SILVER: Record<ThemeMode, string> = {
  light: "#94a3b8",
  dark: "#cbd5e1",
};

function pickMarkerPair(count: number, lastFrom: number | null, lastTo: number | null): [number, number] | null {
  if (count < 2) return null;

  for (let attempt = 0; attempt < 12; attempt++) {
    const fromIdx = Math.floor(Math.random() * count);
    let toIdx = Math.floor(Math.random() * count);
    if (toIdx === fromIdx) toIdx = (toIdx + 1) % count;
    if (fromIdx === lastFrom && toIdx === lastTo) continue;
    return [fromIdx, toIdx];
  }

  const fromIdx = Math.floor(Math.random() * count);
  const toIdx = (fromIdx + 1 + Math.floor(Math.random() * (count - 1))) % count;
  return [fromIdx, toIdx];
}

function quadPoint(
  x1: number,
  y1: number,
  qx: number,
  qy: number,
  x2: number,
  y2: number,
  t: number,
) {
  const u = 1 - t;
  return {
    x: u * u * x1 + 2 * u * t * qx + t * t * x2,
    y: u * u * y1 + 2 * u * t * qy + t * t * y2,
  };
}

function parseQuadPath(d: string) {
  const match = d.match(/^M\s+([\d.]+)\s+([\d.]+)\s+Q\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)$/);
  if (!match) return null;
  return {
    x1: Number(match[1]),
    y1: Number(match[2]),
    qx: Number(match[3]),
    qy: Number(match[4]),
    x2: Number(match[5]),
    y2: Number(match[6]),
  };
}

function pathOpacity(elapsed: number, duration: number) {
  const t = elapsed / duration;
  if (t < 0.12) return t / 0.12;
  if (t > 0.78) return Math.max(0, (1 - t) / 0.22);
  return 0.85;
}

type GlobeDataPathsProps = {
  mode: ThemeMode;
  markers: GlobeMarker[];
  rotationRef: RefObject<GlobeRotation>;
  maxPaths?: number;
};

export function GlobeDataPaths({ mode, markers, rotationRef, maxPaths = 10 }: GlobeDataPathsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 700, h: 500 });
  const [paths, setPaths] = useState<ActivePath[]>([]);
  const [frame, setFrame] = useState(0);
  const lastPairRef = useRef<{ from: number; to: number } | null>(null);
  const idPrefix = useId().replace(/:/g, "");
  const pathColor = PATH_SILVER[mode];

  const markersRef = useRef(markers);
  markersRef.current = markers;

  const layout = useMemo(() => globeLayout(size.w, size.h), [size.h, size.w]);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 0 && rect.height > 0) {
        setSize({ w: rect.width, h: rect.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let raf = 0;
    const step = () => {
      setFrame((n) => n + 1);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const rotation = rotationRef.current ?? { lambda: 0, phi: 12, gamma: 0 };

  const projectMarker = (marker: GlobeMarker) =>
    projectLngLat(marker.longitude, marker.latitude, rotation, layout.radius, layout.cx, layout.cy);

  const now = typeof performance !== "undefined" ? performance.now() : 0;

  const resolvedPaths = useMemo(() => {
    void frame;
    return paths
      .map((path) => {
        const from = projectMarker(markers[path.fromIdx]!);
        const to = projectMarker(markers[path.toIdx]!);
        if (!from.visible || !to.visible) return null;

        const d = buildMarkerArcPath(from, to, layout.cx, layout.cy);
        const elapsed = (now - path.startedAt) / 1000;
        const quad = parseQuadPath(d);
        const progress = Math.min(1, Math.max(0, elapsed / (path.duration * 0.55)));
        const packet = quad ? quadPoint(quad.x1, quad.y1, quad.qx, quad.qy, quad.x2, quad.y2, progress) : null;

        return {
          ...path,
          d,
          from,
          to,
          opacity: pathOpacity(elapsed, path.duration),
          packet,
          packetOpacity: progress > 0 && progress < 1 ? 1 : 0,
        };
      })
      .filter((path): path is NonNullable<typeof path> => path !== null);
  }, [frame, layout.cx, layout.cy, markers, now, paths, rotation, layout.radius]);

  const spawnPath = () => {
    const currentMarkers = markersRef.current;
    const currentLayout = layoutRef.current;
    const currentRotation = rotationRef.current ?? { lambda: 0, phi: 12, gamma: 0 };

    const pair = pickMarkerPair(
      currentMarkers.length,
      lastPairRef.current?.from ?? null,
      lastPairRef.current?.to ?? null,
    );
    if (!pair) return;

    const [fromIdx, toIdx] = pair;
    const fromMarker = currentMarkers[fromIdx]!;
    const toMarker = currentMarkers[toIdx]!;
    const from = projectLngLat(
      fromMarker.longitude,
      fromMarker.latitude,
      currentRotation,
      currentLayout.radius,
      currentLayout.cx,
      currentLayout.cy,
    );
    const to = projectLngLat(
      toMarker.longitude,
      toMarker.latitude,
      currentRotation,
      currentLayout.radius,
      currentLayout.cx,
      currentLayout.cy,
    );
    if (!from.visible || !to.visible) return;

    lastPairRef.current = { from: fromIdx, to: toIdx };

    const id = `${idPrefix}-${Math.random().toString(36).slice(2, 9)}`;
    const duration = 2.4 + Math.random() * 2.2;
    const startedAt = performance.now();

    setPaths((current) => {
      const next = [...current, { id, fromIdx, toIdx, duration, startedAt }];
      return next.length > maxPaths ? next.slice(-maxPaths) : next;
    });

    window.setTimeout(() => {
      setPaths((current) => current.filter((p) => p.id !== id));
    }, duration * 1000 + 400);
  };

  useEffect(() => {
    if (markers.length < 2) return;

    spawnPath();
    const interval = window.setInterval(spawnPath, 1100 + Math.random() * 900);
    return () => window.clearInterval(interval);
  }, [markers.length, maxPaths, idPrefix]);

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0 z-[2]" aria-hidden>
      <svg className="h-full w-full" viewBox={`0 0 ${size.w} ${size.h}`} preserveAspectRatio="xMidYMid meet">
        <defs>
          <clipPath id={`${idPrefix}-disc`}>
            <circle cx={layout.cx} cy={layout.cy} r={layout.clipRadius} />
          </clipPath>
          <filter id={`${idPrefix}-glow`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g clipPath={`url(#${idPrefix}-disc)`}>
          {resolvedPaths.map((path) => (
            <g key={path.id}>
              <circle cx={path.from.sx} cy={path.from.sy} r={3.2} fill={pathColor} opacity={0.95} />
              <circle cx={path.to.sx} cy={path.to.sy} r={3.2} fill={pathColor} opacity={0.95} />

              <path
                d={path.d}
                fill="none"
                stroke={pathColor}
                strokeWidth={1.4}
                strokeLinecap="round"
                opacity={path.opacity}
                filter={`url(#${idPrefix}-glow)`}
              />

              {path.packet ? (
                <circle
                  cx={path.packet.x}
                  cy={path.packet.y}
                  r={2.2}
                  fill={pathColor}
                  opacity={path.packetOpacity * path.opacity}
                />
              ) : null}
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}
